<template>
  <ValidationObserver
    ref="observer"
    v-slot="{ invalid }"
    tag="div"
  >
    <!--
    For the secondary CTA button - we don't want to disable it if the admin
    is updating a user
    -->
    <ModalConfirmation
      :class="$style.modal"
      :data-test-id="$testId('modalConfirmation')"
      :isDisabledPrimary="invalidDate || (invalid && settings.emailSettings.sendWelcomeEmail)"
      :isDisabledSecondary="isNewUser
        && (invalidDate || (invalid && settings.emailSettings.sendWelcomeEmail))"
      :secondaryButtonProps="secondaryButtonProps"
      :showModal="showModal"
      :title="getTitle"
      @cancel="handleSecondaryAction"
      @close="closeModal"
      @save="saveUser"
    >
      <template v-slot:body>
        <UserSummary
          :item="item"
        />
        <Card>
          <template v-slot:header>
            <HeadingMedium>
              Activation Information
            </HeadingMedium>
          </template>
          <template v-slot:body>
            <NotificationInPage
              :class="$style.notification"
              :data-test-id="$testId('notification')"
              :notification="notification"
              :showNotification="showProTip"
              @close="dismissProTip"
            />
            <SegmentedButton
              :class="$style.activationButtons"
              name="activationButtons"
              :options="$options.activationOptions"
              :value="activationChoice"
              @input="updateActivationChoice"
            />
            <div
              :class="$style.fieldContainer"
            >
              <FieldsScheduleActivation
                v-if="activationChoice !== $options.later"
                v-model="settings"
                :action="$options.activate"
                :data-test-id="$testId('fieldsScheduleActivation')"
                displayInline
                :inMainPanel="false"
                :showDate="showDate"
                @dateIsInvalid="handleDateIsInvalid"
              />
              <ParagraphBody
                v-if="showInformationalText"
                :data-test-id="$testId('informationalText')"
              >
                While staged, the user will have no access to any resources that they are bound to.
                We recommend activating later if you are not yet sure
                when this user will need access.
                You may schedule activation at any point in the future from the User profile or
                from the Users list.
                <Link
                  :data-test-id="$testId('learnMore')"
                  :href="$options.userStatesLink"
                  isExternal
                  target="_blank"
                  text="Learn more about activation and user states"
                />
              </ParagraphBody>
            </div>
          </template>
        </Card>
      </template>
      <template v-slot:footerAddition>
        <Link
          :data-test-id="$testId('emailTemplate')"
          :href="$options.emailLink"
          isExternal
          target="_blank"
          text="Learn how to customize email templates"
        />
      </template>
    </ModalConfirmation>
  </ValidationObserver>
</template>

<script>
import {
  Card,
  HeadingMedium,
  Link,
  ModalConfirmation,
  NotificationInPage,
  ParagraphBody,
  SegmentedButton,
  Types,
} from '@jumpcloud/ui-components';
import { ValidationObserver } from 'vee-validate3';
import { mapActions, mapGetters } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import { userStateActionType } from 'client/systemusers/constants/SystemUserConstants';
import Constants, {
  LocalStorageConsts,
  ModuleConsts,
} from 'client/util/Constants';
import FieldsScheduleActivation from 'client/systemusers/components/FieldsScheduleActivation.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import LocalStorageService from 'client/util/LocalStorageService';
import ModalHelper from 'client/systemusers/Modals/ModalHelper';
import UserSummary from 'client/systemusers/components/UserSummary.vue';

const {
  NotificationConfig,
  SecondaryButtonProps,
} = Types;

const {
  activationOptionTypes,
  generateActivationNotification,
  setDefaultActionDate,
} = ModalHelper;

const { error, success } = Constants;
const { userScheduleStartDateTipDismissed } = LocalStorageConsts;

export default {
  name: 'UserActivationSummaryModal',

  components: {
    Card,
    FieldsScheduleActivation,
    HeadingMedium,
    Link,
    ModalConfirmation,
    NotificationInPage,
    ParagraphBody,
    SegmentedButton,
    UserSummary,
    ValidationObserver,
  },

  props: {
    item: {
      type: Object,
      default: undefined,
    },

    showModal: Boolean,
  },

  data() {
    return {
      activationChoice: this.$options.schedule,
      settings: {
        emailSettings: {
          sendWelcomeEmail: true,
          welcomeEmailAddress: '',
        },
        eventDate: setDefaultActionDate(userStateActionType.activate),
      },
      isDateInvalid: false,
      showProTip: false,
    };
  },

  computed: {
    ...mapGetters('SystemUserModel', {
      userDisplayName: 'displayName',
    }),

    getTitle() {
      return this.isNewUser
        ? 'New User Created!'
        : 'Set Up Activation for this User';
    },

    invalidDate() {
      return this.showDate && this.isDateInvalid;
    },

    isNewUser() {
      return !this.$route.params.id;
    },

    notification() {
      const config = NotificationConfig({
        isMarkdown: true,
        message: "**Pro Tip:** Schedule this user's start date to set up their account before their first day.",
        showCloseButton: true,
        title: '',
        type: 'informational',
      });

      return config;
    },

    secondaryButtonProps() {
      return SecondaryButtonProps({
        text: this.isNewUser ? 'save and create another' : 'skip',
      });
    },

    shouldShowProTip() {
      return this.$options.LocalStorageService
        .getValue(userScheduleStartDateTipDismissed) !== 'seen';
    },

    showDate() {
      return this.activationChoice === this.$options.schedule;
    },

    showEmail() {
      return this.activationChoice !== this.$options.later;
    },

    showInformationalText() {
      return this.activationChoice === this.$options.later;
    },
  },

  LocalStorageService,

  accountStates: ModuleConsts.systemUser.accountStates,

  activate: userStateActionType.activate,

  activationOptions: [
    // These need to be in this order for the button group
    activationOptionTypes.schedule,
    activationOptionTypes.now,
    activationOptionTypes.later,
  ],

  emailLink: KbArticleUrls.emailCustomization,

  later: activationOptionTypes.later.value,
  now: activationOptionTypes.now.value,
  schedule: activationOptionTypes.schedule.value,

  userStatesLink: KbArticleUrls.managingUserStates,

  mounted() {
    this.showProTip = this.shouldShowProTip;
    this.fetchCollection();
  },

  methods: {
    ...mapActions(notificationModule, [
      'triggerErrorNotification',
      'triggerSuccessNotification',
    ]),

    ...mapActions('SystemUserCollection', [
      'fetchCollection',
      'scheduleUserStateChange',
      'updateModifiedItemCount',
      'updateNextScheduledEvent',
    ]),

    ...mapActions('SystemUserModel', [
      'activateUser',
      'activateWithEmail',
    ]),

    closeModal() {
      this.$emit('close');
    },

    dismissProTip() {
      this.showProTip = !this.showProTip;
      this.$options.LocalStorageService.setItem(userScheduleStartDateTipDismissed, 'seen');
    },

    handleDateIsInvalid(value) {
      this.isDateInvalid = value;
    },

    /**
     * If this is an update to an existing user, we want to be able to
     * just close the modal and skip this step.
     */
    handleSecondaryAction() {
      if (this.isNewUser) {
        this.saveUserAndCreateNew();
      } else {
        this.closeModal();
        this.$router.push({ name: 'users' });
      }
    },

    /**
     * Display notification to admin depending on action
     */
    notifyAdmin(type, option) {
      const { message, title } = generateActivationNotification(type, option, this.userDisplayName);

      if (type === success) {
        this.triggerSuccessNotification({ title, message });
      } else {
        this.triggerErrorNotification({ title, message });
      }
    },

    /**
     * Schedule activation: Create a scheduled event for the user
     * Activate now: Immediately activate the user
     * Activate later: Close the modal without other action
     */
    async saveUser() {
      if (this.activationChoice === this.$options.schedule) {
        const results = await this.scheduleUserStateChange({
          payload: {
            action: this.$options.activate,
            emailSettings: this.settings.emailSettings,
            eventDate: this.settings.eventDate,
            ids: [this.item._id],
          },
        });

        const { failedJobs, scheduledUsers } = results;

        if (scheduledUsers.length) {
          // Update scheduled events list so that event badge displays
          this.updateNextScheduledEvent([this.item._id]);
          this.notifyAdmin(success, this.$options.schedule);
        } else if (failedJobs.length) {
          this.notifyAdmin(error, this.$options.schedule);
        }
      } else if (this.activationChoice === this.$options.now) {
        try {
          const { emailSettings } = this.settings;
          await this.activateUser();

          if (emailSettings.sendWelcomeEmail) {
            await this.activateWithEmail(emailSettings.welcomeEmailAddress);
          }
          this.notifyAdmin(success, this.$options.now);
        } catch (err) {
          this.notifyAdmin(error, this.$options.now);
        }
      } else {
        // In this case, don't do anything, just give the admin a message
        this.notifyAdmin(success, this.$options.later);
      }

      // Let any components watching know that this user has been updated
      this.updateModifiedItemCount();

      this.closeModal();
      this.$router.push({ name: 'users' });
    },

    async saveUserAndCreateNew() {
      await this.saveUser();
      this.$router.push({ name: 'newUser' });
    },

    updateActivationChoice(value) {
      this.activationChoice = value;
    },
  },
};
</script>

<style module scoped>
.modal.modal {
  --modal-width: 95ch;
  --modal-max-width: 95ch;
}

.notification {
  margin-bottom: var(--jc-spacer-medium);
}

/* Reach in and set the max-width so that the button text doesn't wrap */
.activationButtons /deep/ div {
  max-width: 62ch;
}

.fieldContainer {
  display: grid;
  min-height: var(--jc-size-14);
}
</style>
